import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Paper, Typography, Alert, Button } from "@mui/material";
import { QrCodeScanner as ScanIcon } from "@mui/icons-material";
import jsQR from "jsqr";

// Resolves scanned QR content to an in-app route to navigate to. Accepts
// either a bare traveler token, or a full deep-link URL (traveler, BOM, or
// any future one) like https://app.kodexshieldingsolutions.com/shop/bom?project=16
// — any parseable URL's path + query is followed directly, so new deep
// link types don't need a matcher added here.
function resolveScanTarget(decodedText) {
    const text = decodedText.trim();

    if (/^[0-9a-fA-F-]{36}$/.test(text)) {
        return `/traveler/${text}`;
    }

    try {
        const url = new URL(text);
        return `${url.pathname.replace(/\/$/, "")}${url.search}`;
    } catch {
        return null;
    }
}

// In timesheet mode (opened from the Timesheets page) any traveler QR — the
// "Log my hours" one or the "Sign off step" one — goes to the hours form only,
// never the traveler's approval page. Anything else isn't accepted.
function toTimeEntryTarget(target) {
    const match = target?.match(/^\/(?:traveler|log-time)\/([0-9a-fA-F-]{36})$/);
    return match ? `/log-time/${match[1]}` : null;
}

export default function ScanQR() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const timeMode = searchParams.get("mode") === "time";
    const videoRef = useRef(null);
    const canvasRef = useRef(document.createElement("canvas"));
    const streamRef = useRef(null);
    const frameRef = useRef(null);
    const [error, setError] = useState("");
    const [wrongCode, setWrongCode] = useState(false);
    const [scanning, setScanning] = useState(true);

    useEffect(() => {
        let cancelled = false;

        async function start() {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: "environment" },
                });
                if (cancelled) {
                    stream.getTracks().forEach((t) => t.stop());
                    return;
                }
                streamRef.current = stream;
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    await videoRef.current.play();
                }
                tick();
            } catch {
                setError("Couldn't access the camera. Check your browser permissions.");
            }
        }

        function tick() {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            if (video && video.readyState === video.HAVE_ENOUGH_DATA) {
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const code = jsQR(imageData.data, canvas.width, canvas.height);
                if (code?.data) {
                    const resolved = resolveScanTarget(code.data);
                    const target = timeMode ? toTimeEntryTarget(resolved) : resolved;
                    if (resolved && !target) {
                        setWrongCode(true);
                    }
                    if (target) {
                        setScanning(false);
                        navigate(target, { replace: true });
                        return;
                    }
                }
            }
            frameRef.current = requestAnimationFrame(tick);
        }

        start();

        return () => {
            cancelled = true;
            if (frameRef.current) cancelAnimationFrame(frameRef.current);
            streamRef.current?.getTracks().forEach((t) => t.stop());
        };
    }, [navigate, timeMode]);

    return (
        <Box sx={{ p: 2, maxWidth: 520, mx: "auto" }}>
            <Paper elevation={0} sx={{ p: 2.5, border: 1, borderColor: "divider", borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ScanIcon /> Scan QR Code
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    {timeMode
                        ? "Point the camera at the QR code on the work order traveler to log your hours."
                        : "Point the camera at the QR code on a printed traveler or BOM."}
                </Typography>

                {error ? (
                    <Alert severity="error" sx={{ mt: 1.5 }}>
                        {error}
                    </Alert>
                ) : (
                    <Box
                        sx={{
                            mt: 1.5,
                            borderRadius: 2,
                            overflow: "hidden",
                            bgcolor: "black",
                            aspectRatio: "1 / 1",
                            position: "relative",
                        }}
                    >
                        <video
                            ref={videoRef}
                            playsInline
                            muted
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                        {scanning && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: "15%",
                                    border: "2px solid",
                                    borderColor: "primary.main",
                                    borderRadius: 2,
                                    pointerEvents: "none",
                                }}
                            />
                        )}
                    </Box>
                )}

                {wrongCode && !error && (
                    <Alert severity="warning" sx={{ mt: 1.5 }}>
                        That isn't a work order traveler QR code. Scan the QR on the traveler sheet.
                    </Alert>
                )}

                <Button fullWidth sx={{ mt: 2 }} onClick={() => navigate(-1)}>
                    Cancel
                </Button>
            </Paper>
        </Box>
    );
}
